import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FileText, RefreshCw, Trash2, Upload } from 'lucide-react';
import registryApi, { REGISTRY_GROUPS } from '../../api/registryApi';
import { selectUserEmail } from '../Login/loginSlice';

const getFileName = (file) => {
    if (typeof file === 'string') return file;
    return file?.fileName || file?.name || file?.originalName || file?.originalname || file?.filename || '';
};

const normalizeFiles = (data) => {
    const source = Array.isArray(data)
        ? data
        : data?.files || data?.items || data?.data || data?.result || [];

    return Array.isArray(source) ? source : [];
};

const RegistryManager = () => {
    const [group, setGroup] = useState(REGISTRY_GROUPS[0].value);
    const [files, setFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deletingFileName, setDeletingFileName] = useState('');
    const [message, setMessage] = useState('');
    const userEmail = useSelector(selectUserEmail);
    const fileInputRef = useRef(null);

    const selectedGroupLabel = useMemo(() => {
        return REGISTRY_GROUPS.find((item) => item.value === group)?.label || group;
    }, [group]);

    const fetchFiles = useCallback(async (nextGroup = group) => {
        if (!userEmail) {
            setFiles([]);
            setMessage('Не удалось определить email пользователя');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await registryApi.listFiles(nextGroup, userEmail);
            setFiles(normalizeFiles(response.data));
        } catch (error) {
            console.error('Registry list error:', error);
            setFiles([]);
            setMessage('Не удалось получить список файлов');
        } finally {
            setLoading(false);
        }
    }, [group, userEmail]);

    const handleGroupChange = (event) => {
        const nextGroup = event.target.value;
        setGroup(nextGroup);
        setSelectedFiles([]);
    };

    const handleFileChange = (event) => {
        setSelectedFiles(Array.from(event.target.files || []));
        setMessage('');
    };

    const handleUpload = async () => {
        if (!selectedFiles.length) {
            setMessage('Выберите один или несколько файлов');
            return;
        }

        if (!userEmail) {
            setMessage('Не удалось определить email пользователя');
            return;
        }

        setUploading(true);
        setMessage('');

        try {
            await registryApi.uploadFiles(group, selectedFiles, userEmail);
            setSelectedFiles([]);
            if (fileInputRef.current) fileInputRef.current.value = '';
            setMessage('Файлы загружены');
            fetchFiles();
        } catch (error) {
            console.error('Registry upload error:', error);
            setMessage('Не удалось загрузить файлы');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (fileName) => {
        if (!fileName) return;

        if (!userEmail) {
            setMessage('Не удалось определить email пользователя');
            return;
        }

        setDeletingFileName(fileName);
        setMessage('');

        try {
            await registryApi.deleteFile(group, fileName, userEmail);
            setFiles((prevFiles) => prevFiles.filter((file) => getFileName(file) !== fileName));
            setMessage('Файл удален');
        } catch (error) {
            console.error('Registry delete error:', error);
            setMessage('Не удалось удалить файл');
        } finally {
            setDeletingFileName('');
        }
    };

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    return (
        <div className="registry-manager">
            <div className="registry-header">
                <div>
                    <span className="card-title">Реестр файлов</span>
                    <div className="registry-subtitle">{selectedGroupLabel}</div>
                </div>
                <button
                    className="registry-icon-button"
                    type="button"
                    onClick={() => fetchFiles()}
                    disabled={loading}
                    title="Обновить список"
                >
                    <RefreshCw size={18} />
                    <span>Обновить</span>
                </button>
            </div>

            <div className="registry-controls">
                <select
                    className="registry-select"
                    value={group}
                    onChange={handleGroupChange}
                    disabled={loading || uploading}
                >
                    {REGISTRY_GROUPS.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}
                </select>

                <label className="registry-file-picker">
                    <Upload size={16} />
                    <span>Выбрать файлы</span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileChange}
                    />
                </label>

                <button
                    className="registry-upload-button"
                    type="button"
                    onClick={handleUpload}
                    disabled={uploading || !selectedFiles.length}
                >
                    {uploading ? 'Загрузка...' : 'Загрузить'}
                </button>
            </div>

            {selectedFiles.length > 0 && (
                <div className="registry-selected">
                    {selectedFiles.map((file) => file.name).join(', ')}
                </div>
            )}

            {message && <div className="registry-message">{message}</div>}

            <div className="registry-file-list">
                {loading ? (
                    <div className="registry-empty">Загрузка списка...</div>
                ) : files.length ? (
                    files.map((file) => {
                        const fileName = getFileName(file);
                        return (
                            <div className="registry-file-row" key={fileName}>
                                <div className="registry-file-name">
                                    <FileText size={16} />
                                    <span title={fileName}>{fileName}</span>
                                </div>
                                <button
                                    className="registry-delete-button"
                                    type="button"
                                    onClick={() => handleDelete(fileName)}
                                    disabled={deletingFileName === fileName}
                                    title="Удалить файл"
                                >
                                    <Trash2 size={16} />
                                    <span>Удалить</span>
                                </button>
                            </div>
                        );
                    })
                ) : (
                    <div className="registry-empty">Файлов пока нет</div>
                )}
            </div>
        </div>
    );
};

export default RegistryManager;
